import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { IErpBranchMappingData, useGetErpBranchMappingsApi } from 'apis/erp-mappings/use-get-erp-branch-mappings-api';
import { useUpdateErpBranchMappingApi } from 'apis/erp-mappings/use-update-erp-branch-mapping-api';
import React, { useState } from 'react';

interface IBranchMappingEditModal {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: number;
  supplierId: number;
  supplierType: string;
}

export const BranchMappingEditModal: React.FC<IBranchMappingEditModal> = ({
  isOpen,
  onClose,
  restaurantId,
  supplierId,
  supplierType,
}) => {
  const toast = useCustomToast();
  const [editedMappings, setEditedMappings] = useState<IErpBranchMappingData[]>([]);

  const {
    data: branchMappingsData,
    isLoading: isLoadingBranchMappings,
    refetch: refetchBranchMappings,
  } = useGetErpBranchMappingsApi({
    enabled: isOpen,
    restaurantId,
    supplierId,
    supplierType,
  });

  const updateMappingMutation = useUpdateErpBranchMappingApi({
    onSuccess: () => {
      toast({
        description: 'Branch mappings updated successfully',
        status: 'success',
      });
      refetchBranchMappings();
      onClose();
    },
  });

  React.useEffect(() => {
    if (branchMappingsData?.data) {
      setEditedMappings([...branchMappingsData.data]);
    }
  }, [branchMappingsData]);

  const handleErpBranchIdChange = (index: number, value: string) => {
    const updated = [...editedMappings];
    updated[index] = { ...updated[index], erpBranchId: value };
    setEditedMappings(updated);
  };

  const handleSave = () => {
    const mappings = editedMappings.map((item) => ({
      subEntityId: item.id,
      subEntityType: item.type,
      erpBranchId: item.erpBranchId || '',
    }));

    updateMappingMutation.mutate({
      body: {
        restaurantId,
        supplierId,
        supplierType,
        mappings,
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Branch Mappings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoadingBranchMappings ? (
            <Text>Loading branch mappings...</Text>
          ) : (
            <VStack spacing={4} align='stretch'>
              {editedMappings.map((mapping, index) => (
                <Box key={mapping.id} p={4} border='1px' borderColor='gray.200' borderRadius='md'>
                  <SimpleGrid columns={2} spacing={4}>
                    <VStack align='start' spacing={2}>
                      <Text fontWeight='medium'>{mapping.name}</Text>
                      <Text fontSize='sm' color='gray.500'>
                        ID: {mapping.id} | Type: {mapping.type}
                      </Text>
                    </VStack>
                    <FormControl>
                      <FormLabel fontSize='sm'>ERP Branch ID</FormLabel>
                      <Input
                        value={mapping.erpBranchId || ''}
                        onChange={(e) => handleErpBranchIdChange(index, e.target.value)}
                        placeholder='Enter ERP Branch ID'
                        size='sm'
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSave}
              isLoading={updateMappingMutation.isLoading}
              loadingText='Saving...'>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
